import React, { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './layout/Sidebar';
import Topbar from './layout/Topbar';

const STORAGE_KEY = 'mb.sidebar.collapsed';

/**
 * Application shell: fixed sidebar + sticky topbar + scrolling page container.
 * Desktop keeps the sidebar docked (collapsible to icons); below `lg` it becomes
 * an overlay drawer so the content keeps the full width.
 */
const Layout = () => {
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    });
    const [mobileOpen, setMobileOpen] = useState(false);

    const toggleSidebar = useCallback(() => {
        if (window.matchMedia('(max-width: 1023px)').matches) {
            setMobileOpen((v) => !v);
            return;
        }
        setCollapsed((v) => {
            const next = !v;
            try {
                localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
            } catch {
                /* storage unavailable — collapse still works for this session */
            }
            return next;
        });
    }, []);

    // Escape closes the mobile drawer.
    useEffect(() => {
        if (!mobileOpen) return undefined;
        const onKey = (e) => e.key === 'Escape' && setMobileOpen(false);
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [mobileOpen]);

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-body)' }}>
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-toast focus:btn focus:btn-primary"
            >
                Skip to content
            </a>

            <Sidebar
                collapsed={collapsed}
                mobileOpen={mobileOpen}
                onCloseMobile={() => setMobileOpen(false)}
            />

            <div
                className="app-main transition-[padding] duration-200 ease-out"
                style={{ paddingLeft: 'var(--main-offset, 0px)' }}
            >
                <Topbar onToggleSidebar={toggleSidebar} sidebarCollapsed={collapsed} />

                <main
                    id="main-content"
                    className="animate-fade-in"
                    style={{ padding: 'var(--page-pad-y) var(--page-pad-x) 32px' }}
                >
                    <Outlet />
                </main>
            </div>

            {/* Drives the content offset without a re-render per breakpoint. */}
            <style>{`
                :root { --main-offset: 0px; }
                @media (min-width: 1024px) {
                    :root { --main-offset: ${collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)'}; }
                }
            `}</style>
        </div>
    );
};

export default Layout;
